import {
  ActionGroup,
  Alert,
  Button,
  Form,
  FormAlert,
  FormGroup,
  TextInput,
  LoginMainFooterBandItem,
  LoginPage,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { NavLink, useParams } from 'react-router-dom'

import bgImgSrcSm from '@app/assets/images/intake-login-bg-sm.png'
import bgImgSrcXSm from '@app/assets/images/intake-login-bg-xsm.png'
import bgImgSrc from '@app/assets/images/intake-login-bg.png'
import logoImgSrc from '@app/assets/images/intake-logo-landing.png'
import type { AppState } from '@app/state'
import type { State as AuthState} from '@app/state/authenticate';
import * as Auth from '@app/state/authenticate'
import { AuthActionStatus } from '@app/state/authenticate'

interface RootParams {
  requestId: string
}

export const ResetPasswordScreen = () => {
  const dispatch = useDispatch()

  const [password, setPassword] = useState<string>('')
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>('')
  const [passwordsMatch, setPasswordsMatch] = useState<boolean>(false)

  const { requestId } = useParams<RootParams>()

  const { errorMessage, passwordResetStatus } = useSelector<
    AppState,
    AuthState
  >((state) => {
    return state.auth
  }, shallowEqual)

  const handlePasswordUpdate = useCallback(
    (value: string) => {
      setPassword(value)
      setPasswordsMatch(passwordConfirmation === value)
    },
    [passwordConfirmation],
  )

  const handlePasswordConfirmation = useCallback(
    (value: string) => {
      setPasswordConfirmation(value)
      setPasswordsMatch(password === value)
    },
    [password],
  )

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (passwordsMatch && requestId) {
        dispatch(Auth.actions.resetPassword(requestId, password))
      }
    },
    [dispatch, password, passwordsMatch, requestId],
  )

  const formAlert = useCallback(() => {
    if (passwordResetStatus === AuthActionStatus.Failed) {
      return (
        <FormAlert>
          <Alert
            variant="danger"
            title={errorMessage}
            aria-live="polite"
            isInline
          />
        </FormAlert>
      )
    } else {
      return null
    }
  }, [errorMessage, passwordResetStatus])

  const returnToLoginPage = (
    <LoginMainFooterBandItem>
      <NavLink exact to={'/'} activeClassName="pf-m-current">
        {passwordResetStatus === AuthActionStatus.Succeeded
          ? 'Sign in here'
          : 'Remember your password? Sign in here'}
      </NavLink>
    </LoginMainFooterBandItem>
  )

  const requestPasswordForm = (
    <Form onSubmit={handleSubmit}>
      {formAlert()}
      <FormGroup label="New Password" isRequired fieldId="new-password">
        <TextInput
          value={password}
          isRequired
          type="password"
          aria-desribedby="new-password"
          id="new-password"
          name="new-password"
          onChange={handlePasswordUpdate}
        />
      </FormGroup>
      <FormGroup label="Confirm Password" isRequired fieldId="confirm-password">
        <TextInput
          value={passwordConfirmation}
          isRequired
          type="password"
          aria-desribedby="confirm-password"
          id="confirm-password"
          name="confirm-password"
          onChange={handlePasswordConfirmation}
        />
      </FormGroup>
      <ActionGroup>
        <Button
          type="submit"
          isBlock
          variant="primary"
          isDisabled={!passwordsMatch}
        >
          Set New Password
        </Button>
      </ActionGroup>
    </Form>
  )

  const images = {
    lg: bgImgSrc,
    sm: bgImgSrcSm,
    sm2x: bgImgSrcSm,
    xs: bgImgSrcXSm,
    xs2x: bgImgSrcXSm,
  }

  const requestPasswordSubtitle = useCallback(() => {
    if (passwordResetStatus === AuthActionStatus.Succeeded) {
      return 'Password successfully updated.'
    } else {
      return 'Please enter a new password for your account.'
    }
  }, [passwordResetStatus])

  return (
    <LoginPage
      brandImgSrc={logoImgSrc}
      loginTitle="Reset your password"
      loginSubtitle={requestPasswordSubtitle()}
      backgroundImgSrc={images}
      forgotCredentials={returnToLoginPage}
    >
      {passwordResetStatus === AuthActionStatus.Succeeded
        ? null
        : requestPasswordForm}
    </LoginPage>
  )
}
